import { formatDateTime } from "../dates";
import { alphaMutationRejected } from "../alpha-mutation";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { api } from "../api";
import { ErrorState, Loading } from "../ui/State";
export function WhatsAppAlpha() {
  const [q] = useSearchParams(),
    building = q.get("building"),
    [model, setModel] = useState<any>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    pending = useRef<{ url: string; body: any } | null>(null);
  const load = () =>
    building
      ? api("/api/alpha/whatsapp?building=" + building).then(setModel)
      : Promise.resolve();
  useEffect(() => {
    setModel(undefined);
    void load().catch(setError);
  }, [building]);
  async function save(url: string, body: any) {
    pending.current ??= {
      url,
      body: { ...body, idempotencyKey: crypto.randomUUID() },
    };
    setBusy(true);
    try {
      await api(pending.current.url, {
        method: "POST",
        body: JSON.stringify(pending.current.body),
      });
      pending.current = null;
      setError(undefined);
      await load();
    } catch (e) {
      if (alphaMutationRejected(e)) pending.current = null;
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <h1>WhatsApp correspondence</h1>
      {Boolean(error) && <ErrorState error={error} />}{" "}
      {pending.current && !busy && (
        <button onClick={() => void save("", {})}>
          Retry the same request
        </button>
      )}
      {!building ? (
        <p>Choose a building.</p>
      ) : !model ? (
        <Loading />
      ) : (
        <>
          {!model.sendingConfigured && (
            <p>
              Sending is disabled. Account credentials and approved templates
              require administrator setup before provider acceptance testing.
            </p>
          )}
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              void save("/api/alpha/whatsapp/consent", {
                building,
                contactId: f.get("contact"),
                allowed: f.get("allowed") === "yes",
                evidence: f.get("evidence"),
              });
            }}
          >
            <fieldset disabled={busy || !!pending.current}>
              <legend>Record communication consent</legend>
              <label>
                Contact
                <select name="contact" required>
                  <option value="">Choose a contact</option>
                  {model.contacts.map((c: any) => (
                    <option key={c.id} value={c.id}>
                      {c.display_name} · {c.normalised_value}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Preference
                <select name="allowed">
                  <option value="yes">
                    Consented to operational WhatsApp messages
                  </option>
                  <option value="no">Withdraw consent</option>
                </select>
              </label>
              <label>
                When and how consent was obtained or withdrawn
                <textarea
                  name="evidence"
                  required
                  minLength={10}
                  maxLength={2000}
                />
              </label>
              <button>Save consent</button>
            </fieldset>
          </form>
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget),
                t = model.templates[Number(f.get("template"))];
              if (t)
                void save("/api/alpha/whatsapp/prepare", {
                  building,
                  accountId: f.get("account"),
                  contactId: f.get("contact"),
                  template: t.name,
                  language: t.language,
                  parameters: String(f.get("parameters"))
                    .split("\n")
                    .map((s) => s.trim())
                    .filter(Boolean),
                });
            }}
          >
            <fieldset
              disabled={busy || !!pending.current || !model.templates.length}
            >
              <legend>Prepare a message</legend>
              <label>
                Sending account
                <select name="account" required>
                  <option value="">Choose account</option>
                  {model.accounts.map((a: any) => (
                    <option key={a.id} value={a.id}>
                      {a.display_name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Recipient
                <select name="contact" required>
                  <option value="">Choose contact</option>
                  {model.contacts.map((c: any) => (
                    <option key={c.id} value={c.id}>
                      {c.display_name} · {c.normalised_value}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Approved template
                <select name="template" required>
                  <option value="">Choose template</option>
                  {model.templates.map((t: any, i: number) => (
                    <option key={i} value={i}>
                      {t.name} · {t.language} · {t.body}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Template values — one per line, in order
                <textarea name="parameters" />
              </label>
              <button>Prepare preview</button>
            </fieldset>
          </form>
          {model.drafts.map((d: any) => (
            <section className="panel" key={d.id}>
              <h2>
                {d.template_name} · {d.state}
              </h2>
              <p>{d.body_text}</p>
              <p>{d.last_error}</p>
              {d.delivery.map((e: any, i: number) => (
                <p key={i}>
                  {e.status} · {formatDateTime(new Date(Number(e.at) * 1000))}
                </p>
              ))}
              {d.state === "Draft" && (
                <button
                  disabled={busy || !!pending.current}
                  onClick={() =>
                    void save("/api/alpha/whatsapp/" + d.id + "/approve", {})
                  }
                >
                  Approve this preview
                </button>
              )}
              {d.state === "Approved" && (
                <button
                  disabled={
                    busy || !!pending.current || !model.sendingConfigured
                  }
                  onClick={() =>
                    void save("/api/alpha/whatsapp/" + d.id + "/send", {})
                  }
                >
                  Send approved WhatsApp message
                </button>
              )}
              {["Uncertain", "Dispatching"].includes(d.state) && (
                <p>
                  Do not resend. Reconcile this attempt with the provider first.
                </p>
              )}
            </section>
          ))}
        </>
      )}
    </>
  );
}
